// Frontend-owned navigation source of truth: drives both rendered menu
// sections (useNavAuthMenu, dashboard sidebar) and route access
// (route-access.ts). The API only ever answers "who is this user and what may
// they do?" via `roles` and `permissions`; this module decides what that looks
// like and which routes each capability unlocks.

import {
  BanknotesIcon,
  Bars3BottomLeftIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  HeartIcon,
  ReceiptPercentIcon,
  Squares2X2Icon,
  TagIcon,
  UserCircleIcon,
  UsersIcon
} from '@heroicons/vue/24/outline'
import { Permission, NavMenuSection, UserRole } from '~/enums'
import type { NavDefinitionItem } from './NavDefinitionItem'

// Order mirrors the deleted backend definitions' own row order.
export const NAV_ITEMS: readonly NavDefinitionItem[] = [
  {
    code: 'dashboard',
    navMenuCode: 'DASHBOARD',
    icon: Squares2X2Icon,
    route: '/dashboard',
    section: NavMenuSection.MAIN,
    order: 0,
    permissions: [],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'orders',
    navMenuCode: 'ORDERS',
    icon: ReceiptPercentIcon,
    route: '/dashboard/orders',
    section: NavMenuSection.MAIN,
    order: 1,
    permissions: [Permission.VIEW_ORDERS],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'orders-customer',
    navMenuCode: 'ORDERS_CUSTOMER',
    icon: ReceiptPercentIcon,
    route: '/dashboard/orders',
    section: NavMenuSection.MAIN,
    order: 1,
    permissions: [Permission.VIEW_ORDERS],
    roles: [UserRole.CUSTOMER]
  },
  {
    code: 'payouts',
    navMenuCode: 'PAYOUTS',
    icon: BanknotesIcon,
    route: '/dashboard/payouts',
    section: NavMenuSection.MAIN,
    order: 2,
    permissions: [Permission.VIEW_PAYOUTS],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER]
  },
  {
    code: 'reports',
    navMenuCode: 'REPORTS',
    icon: ChartBarIcon,
    route: '/dashboard/reports',
    section: NavMenuSection.MAIN,
    order: 3,
    permissions: [Permission.VIEW_REPORTS],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'payments',
    navMenuCode: 'PAYMENTS',
    icon: CreditCardIcon,
    route: '/dashboard/payments',
    section: NavMenuSection.MAIN,
    order: 4,
    permissions: [Permission.VIEW_PAYMENTS],
    roles: [UserRole.CUSTOMER]
  },
  {
    code: 'favorites',
    navMenuCode: 'FAVORITES',
    icon: HeartIcon,
    route: '/dashboard/favorites',
    section: NavMenuSection.MAIN,
    order: 5,
    permissions: [Permission.VIEW_STORES],
    roles: [UserRole.CUSTOMER]
  },
  {
    code: 'reviews-customer',
    navMenuCode: 'REVIEWS_CUSTOMER',
    icon: ChatBubbleBottomCenterTextIcon,
    route: '/dashboard/reviews',
    section: NavMenuSection.MAIN,
    order: 6,
    permissions: [Permission.VIEW_ORDERS],
    roles: [UserRole.CUSTOMER]
  },
  {
    code: 'brands',
    navMenuCode: 'BRANDS',
    icon: TagIcon,
    route: '/dashboard/brands',
    section: NavMenuSection.SECONDARY,
    order: 7,
    permissions: [Permission.VIEW_BRANDS],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER]
  },
  {
    code: 'stores',
    navMenuCode: 'STORES',
    icon: BuildingStorefrontIcon,
    route: '/dashboard/stores',
    section: NavMenuSection.SECONDARY,
    order: 8,
    permissions: [Permission.VIEW_STORES],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'reviews',
    navMenuCode: 'REVIEWS',
    icon: ChatBubbleBottomCenterTextIcon,
    route: '/dashboard/reviews',
    section: NavMenuSection.SECONDARY,
    order: 9,
    permissions: [Permission.VIEW_STORES],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'menus',
    navMenuCode: 'MENUS',
    icon: Bars3BottomLeftIcon,
    route: '/dashboard/menus',
    section: NavMenuSection.SECONDARY,
    order: 10,
    permissions: [Permission.VIEW_MENUS],
    roles: [UserRole.ACCOUNT_OWNER, UserRole.PARTNER_MANAGER, UserRole.STORE_MANAGER]
  },
  {
    code: 'users',
    navMenuCode: 'USERS',
    icon: UsersIcon,
    route: '/dashboard/users',
    section: NavMenuSection.SECONDARY,
    order: 11,
    // Staff hold `view users`, organizer customers hold `view organizer users`.
    permissions: [Permission.VIEW_USERS, Permission.VIEW_ORGANIZER_USERS],
    roles: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.ACCOUNT_OWNER,
      UserRole.PARTNER_MANAGER,
      UserRole.CUSTOMER
    ]
  },
  {
    code: 'profile',
    navMenuCode: 'PROFILE',
    icon: UserCircleIcon,
    route: '/dashboard/settings/profile',
    section: NavMenuSection.USER,
    order: 12,
    permissions: [],
    roles: [
      UserRole.ACCOUNT_OWNER,
      UserRole.PARTNER_MANAGER,
      UserRole.STORE_MANAGER,
      UserRole.CUSTOMER
    ]
  },
  {
    code: 'settings',
    navMenuCode: 'SETTINGS',
    icon: Cog6ToothIcon,
    route: '/dashboard/settings',
    section: NavMenuSection.USER,
    order: 13,
    permissions: [],
    roles: [
      UserRole.ACCOUNT_OWNER,
      UserRole.PARTNER_MANAGER,
      UserRole.STORE_MANAGER,
      UserRole.CUSTOMER
    ]
  }
]
